import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Lock } from 'lucide-react';
import { openBookingModal } from './BookingModal';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from './LanguageToggle';
import LightSwitch from './LightSwitch';
import { Link } from 'react-router-dom';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_escape-nexus/artifacts/6l2bsu58_key-2-final-escaped%20%281%29.png';

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { t, lang } = useLanguage();

  const NAV_LINKS = [
    { label: t('rooms'), href: '#rooms' },
    { label: t('howItWorks'), href: '#how-it-works' },
    { label: t('locations'), href: '#locations' },
    { label: t('contact'), href: '#contact' },
  ];

  const PAGE_LINKS = [
    { label: lang === 'ja' ? 'アクセス' : 'Directions', to: '/directions' },
    { label: lang === 'ja' ? 'FAQ' : 'FAQ', to: '/faq' },
  ];

  return (
    <header
      data-testid="header"
      className="fixed top-0 left-0 right-0 z-50 border-b border-border"
    >
      <div className="absolute inset-0 backdrop-blur-xl bg-background/80" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-32 py-1">
        {/* Logo + Vault */}
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <div id="doomsday-header-slot" className="flex items-center" />
          <a
            href="#hero"
            data-testid="logo"
            className="relative z-10 hover:opacity-80 transition-opacity"
          >
            <img
              src={LOGO_URL}
              alt="OKI-ESCAPE"
              className="h-12 sm:h-[6.5rem] w-auto object-contain"
            />
          </a>
          <Link
            to="/admin/login"
            data-testid="vault-link"
            className="relative z-10 w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-secondary/50 flex items-center justify-center hover:bg-brand/10 hover:text-brand transition-colors text-muted-foreground"
            title="Admin"
          >
            <Lock size={12} className="sm:hidden" />
            <Lock size={16} className="hidden sm:block" />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(link => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-${link.href.replace('#', '')}`}
              className="font-heading text-xl tracking-widest uppercase text-muted-foreground hover:text-brand transition-colors"
            >
              {link.label}
            </a>
          ))}
          {PAGE_LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.to.replace('/', '')}`}
              className="font-heading text-xl tracking-widest uppercase text-muted-foreground hover:text-brand transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-4">
          <LanguageToggle className="hidden" />
          <button
            onClick={openBookingModal}
            data-testid="header-book-now-btn"
            className="hidden sm:inline-block font-heading text-xl tracking-widest uppercase bg-brand text-white px-8 py-4 rounded-full hover:bg-brand-hover transition-colors neon-glow"
          >
            {t('bookNow')}
          </button>
          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden overflow-hidden bg-background backdrop-blur-xl border-b border-border fixed inset-x-0 top-16 sm:top-32 bottom-0 z-50"
          >
            <div className="px-6 py-8 flex flex-col gap-4">
              {NAV_LINKS.map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="font-heading text-lg tracking-widest uppercase text-muted-foreground hover:text-brand transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              {PAGE_LINKS.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="font-heading text-lg tracking-widest uppercase text-muted-foreground hover:text-brand transition-colors py-2"
                >
                  {link.label}
                </Link>
              ))}
              <button
                onClick={() => { setMobileOpen(false); openBookingModal(); }}
                data-testid="mobile-book-now-btn"
                className="font-heading text-lg tracking-widest uppercase bg-brand text-white px-5 py-3 rounded-full hover:bg-brand-hover transition-colors text-center neon-glow"
              >
                {t('bookNow')}
              </button>
              <div className="flex items-center justify-center gap-6 pt-4 border-t border-border mt-2">
                <LightSwitch />
                <LanguageToggle />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
